import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useDepositCacheInvalidator } from '@/hooks/useContractDeposits';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import TransactionSelector from '@/components/ui/TransactionSelector';

interface CreateDepositSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export default function CreateDepositSheet({
    open,
    onOpenChange,
}: CreateDepositSheetProps) {
    const invalidateDepositCaches = useDepositCacheInvalidator();
    const [transactionId, setTransactionId] = useState<number | null>(null);
    const [transactionAmount, setTransactionAmount] = useState(0);
    const [remarks, setRemarks] = useState('');

    const createMutation = useMutation({
        mutationFn: async () => {
            if (!transactionId) throw new Error('Please select a transaction');

            const { error } = await supabase
                .from('contract_deposits')
                .insert({
                    transaction_id: transactionId,
                    remarks: remarks.trim() || null,
                    // contract_id intentionally omitted — will be linked later during Contract Setup
                });

            if (error) throw error;
        },
        onSuccess: () => {
            toast.success('Deposit recorded');
            invalidateDepositCaches();
            handleClose();
        },
        onError: (e: Error) => toast.error(e.message),
    });

    const handleClose = () => {
        setTransactionId(null);
        setTransactionAmount(0);
        setRemarks('');
        onOpenChange(false);
    };

    return (
        <Sheet open={open} onOpenChange={handleClose}>
            <SheetContent
                side="right"
                className="!inset-y-4 !right-4 !h-auto !w-[40vw] !max-w-none rounded-2xl flex flex-col shadow-2xl border bg-background"
            >
                <SheetHeader>
                    <SheetTitle>Record Deposit</SheetTitle>
                </SheetHeader>

                <div className="flex-1 mt-6 space-y-6">
                    {/* Transaction Selector */}
                    <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Bank Transaction *</Label>
                        <TransactionSelector
                            value={transactionId}
                            onChange={(id, transaction) => {
                                setTransactionId(id);
                                setTransactionAmount(transaction?.amount || 0);
                            }}
                            placeholder="Select a deposit transaction..."
                            defaultType="CREDIT"
                        />
                        <p className="text-xs text-muted-foreground">
                            Select the bank transaction that represents this deposit payment
                        </p>
                    </div>

                    {/* Amount preview */}
                    {transactionId && transactionAmount > 0 && (
                        <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900/40">
                            <span className="text-xs text-muted-foreground block mb-1">Deposit Amount</span>
                            <span className="text-2xl font-bold text-green-600 dark:text-green-400">
                                GHS {transactionAmount.toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                        </div>
                    )}

                    {/* Remarks */}
                    <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Remarks</Label>
                        <Textarea
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            placeholder="Driver name, phone number, address, or any identifying information..."
                            rows={3}
                        />
                        <p className="text-xs text-muted-foreground">
                            Identifying information about the depositor. Useful before a driver record exists.
                        </p>
                    </div>
                </div>

                <div className="pt-4 border-t flex justify-end gap-2">
                    <Button variant="outline" onClick={handleClose}>Cancel</Button>
                    <Button
                        onClick={() => createMutation.mutate()}
                        disabled={!transactionId || createMutation.isPending}
                    >
                        {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Record Deposit
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
}
